import React, { useEffect, useMemo, useState } from "react";
import {
  applyVolumeDiscountPlaceholders,
  calculateVolumeDiscountOfferPrice,
  defaultVolumeDiscountLabelForType,
  getVolumeDiscountLayout,
  getVolumeDiscountOfferUnitCount,
  normalizeVolumeDiscountDiscountType,
  normalizeVolumeDiscountOfferMode,
  normalizeVolumeDiscountOffersLayout,
  clampVolumeDiscountItemsPerRow,
  VOLUME_DISCOUNT_DISCOUNT_TYPE,
  VOLUME_DISCOUNT_OFFER_MODE,
  VOLUME_DISCOUNT_OFFERS_LAYOUT,
} from "../../../shared/volume-discount-template.js";
import {
  isVariantAvailable,
  withOutOfStockLabel,
} from "../product-recommendation/utils/product-variant-utils.js";
import styles from "./VolumeDiscountWidget.module.css";

function formatMoney(amount, currency = "USD", locale = "en-US") {
  const value = Number(amount) || 0;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
}

function resolveProductImageUrl(product) {
  if (!product || typeof product !== "object") return "";
  const featured = product.featured_image;
  if (typeof featured === "string" && featured) return featured;
  if (featured && typeof featured === "object" && featured.src) return featured.src;
  if (typeof product.image === "string" && product.image) return product.image;
  if (product.image && typeof product.image === "object" && product.image.src) {
    return product.image.src;
  }
  const first = Array.isArray(product.images) ? product.images[0] : null;
  if (typeof first === "string" && first) return first;
  if (first && typeof first === "object" && first.src) return first.src;
  return "";
}

function resolveOfferDiscountLabelTemplate(offer, offerMode) {
  const custom = String(offer?.discountLabel || "").trim();
  if (custom) return custom;

  const mode = normalizeVolumeDiscountOfferMode(offerMode);
  if (mode === VOLUME_DISCOUNT_OFFER_MODE.BUY_X_GET_Y) {
    const getType = normalizeVolumeDiscountDiscountType(offer?.getDiscountType);
    if (getType !== VOLUME_DISCOUNT_DISCOUNT_TYPE.NONE) {
      return defaultVolumeDiscountLabelForType(getType);
    }
    return defaultVolumeDiscountLabelForType(offer?.buyDiscountType);
  }
  return defaultVolumeDiscountLabelForType(offer?.discountType);
}

/** Shopify single-SKU products use one "Default Title" variant — not a real choice. */
function getSelectableVariants(product) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  if (variants.length <= 1) return [];

  const meaningful = variants.filter((variant) => {
    if (!variant) return false;
    const title = String(variant.title || variant.name || "")
      .trim()
      .toLowerCase();
    return title && title !== "default title";
  });

  return meaningful.length > 0 ? meaningful : [];
}

/** Admin editor only — Design tab can style selectors without a multi-variant product. */
const DEMO_VARIANTS = Object.freeze([
  { id: "demo-variant-black", title: "Black", available: true },
  { id: "demo-variant-red", title: "Red", available: true },
  { id: "demo-variant-green", title: "Green", available: false },
]);

/** Fallback product image when PDP has none (admin preview / missing media). */
const DEMO_PRODUCT_IMAGE =
  "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-3_large.png";

const MAX_PRODUCT_IMAGE_STACK = 5;

function variantLabel(variant) {
  const title = String(variant?.title || variant?.name || "").trim();
  let base = title;
  if (!title || title.toLowerCase() === "default title") {
    const parts = [variant?.option1, variant?.option2, variant?.option3]
      .map((part) => String(part || "").trim())
      .filter(Boolean);
    base = parts.join(" / ") || "Variant";
  }
  return withOutOfStockLabel(base, variant);
}

function variantUnitPrice(variant, fallback) {
  const raw = Number(variant?.price);
  if (!Number.isFinite(raw)) return fallback;
  // Expect major currency units (same as formatProductPriceFields / other templates).
  return raw;
}

function useCountdown(enabled, durationMinutes) {
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, Math.floor((Number(durationMinutes) || 60) * 60))
  );

  useEffect(() => {
    if (!enabled) return undefined;
    setRemaining(Math.max(0, Math.floor((Number(durationMinutes) || 60) * 60)));
    const id = setInterval(() => {
      setRemaining((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [enabled, durationMinutes]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

/**
 * Volume Discount storefront / editor preview widget.
 * Uses the PDP product price; no products-to-offer catalog.
 */
export default function VolumeDiscountWidget({
  popup,
  product = null,
  unitPrice = 30,
  currency = "USD",
  onOfferSelect,
  /** Fired when selected offer / variants change (theme ATC integration). */
  onSelectionChange,
  /** Admin preview: show sample variant dropdowns for Design styling. */
  previewDemoVariants = false,
}) {
  const layout = useMemo(() => {
    const raw = getVolumeDiscountLayout(popup) || {};
    return {
      ...raw,
      offers: Array.isArray(raw.offers) ? raw.offers : [],
      design: raw.design || {},
    };
  }, [popup]);

  const offerMode = normalizeVolumeDiscountOfferMode(layout.offerMode);
  const offersLayout = normalizeVolumeDiscountOffersLayout(layout.offersLayout);
  const isHorizontal = offersLayout === VOLUME_DISCOUNT_OFFERS_LAYOUT.HORIZONTAL;
  const itemsPerRow = clampVolumeDiscountItemsPerRow(layout.itemsPerRow);
  const showCurrentPageProduct = layout.showCurrentPageProduct === true;
  const productImageUrl = useMemo(() => {
    const fromProduct = resolveProductImageUrl(product);
    return fromProduct || DEMO_PRODUCT_IMAGE;
  }, [product]);
  const selectableVariants = useMemo(
    () => getSelectableVariants(product),
    [product]
  );
  const hasVariantChoices = selectableVariants.length > 0;
  const useDemoVariants = Boolean(previewDemoVariants) && !hasVariantChoices;
  const variantsForUi = useDemoVariants ? DEMO_VARIANTS : selectableVariants;
  const showVariantUi =
    layout.showVariantSelectors !== false &&
    (hasVariantChoices || useDemoVariants);
  const [selectedIndex, setSelectedIndex] = useState(
    Number(layout.defaultSelectedIndex) || 0
  );
  const [selectedVariantIds, setSelectedVariantIds] = useState([]);

  useEffect(() => {
    setSelectedIndex(Number(layout.defaultSelectedIndex) || 0);
  }, [layout.defaultSelectedIndex, offerMode, layout.offers.length]);

  const selected =
    layout.offers[selectedIndex] || layout.offers[0] || null;
  const unitCount = selected
    ? getVolumeDiscountOfferUnitCount(selected, offerMode)
    : 1;

  useEffect(() => {
    if (!showVariantUi) {
      setSelectedVariantIds([]);
      return;
    }
    const fallbackId = String(
      variantsForUi[0]?.id ?? variantsForUi[0]?.variant_id ?? ""
    );
    setSelectedVariantIds((prev) =>
      Array.from({ length: Math.max(1, unitCount) }, (_, index) => {
        const existing = prev[index];
        if (
          existing &&
          variantsForUi.some(
            (variant) =>
              String(variant.id ?? variant.variant_id) === String(existing)
          )
        ) {
          return String(existing);
        }
        return fallbackId;
      })
    );
  }, [showVariantUi, variantsForUi, unitCount, selectedIndex]);

  const pricedUnit = useMemo(() => {
    if (!hasVariantChoices || !selectedVariantIds.length) return unitPrice;
    const prices = selectedVariantIds.map((id) => {
      const match = selectableVariants.find(
        (variant) => String(variant.id ?? variant.variant_id) === String(id)
      );
      return variantUnitPrice(match, unitPrice);
    });
    const sum = prices.reduce((acc, value) => acc + value, 0);
    return prices.length ? sum / prices.length : unitPrice;
  }, [hasVariantChoices, selectedVariantIds, selectableVariants, unitPrice]);

  const selectedPrice = selected
    ? calculateVolumeDiscountOfferPrice(selected, pricedUnit, offerMode)
    : { original: 0, final: 0, saved: 0, savedPercentage: 0 };

  useEffect(() => {
    if (typeof onSelectionChange !== "function") return;
    onSelectionChange({
      offer: selected,
      pricing: selectedPrice,
      quantity: unitCount,
      variantIds: hasVariantChoices ? selectedVariantIds : undefined,
    });
  }, [
    onSelectionChange,
    selected,
    selectedPrice.final,
    selectedPrice.original,
    unitCount,
    hasVariantChoices,
    selectedVariantIds,
  ]);

  const timerText = useCountdown(
    Boolean(layout.countdown?.enabled),
    layout.countdown?.durationMinutes
  );

  const design = layout.design || {};
  const accent = design.accentColor || design.selectedBorderColor || "#7C5CFF";
  const fontFamily =
    !design.fontFamily || design.fontFamily === "theme-default"
      ? "Inter, Segoe UI, system-ui, sans-serif"
      : design.fontFamily;

  const px = (value, fallback) => {
    const n = Number(value);
    return `${Number.isFinite(n) ? n : fallback}px`;
  };

  const showVariantSelectors = showVariantUi;

  const updateVariantAt = (index, variantId) => {
    const nextVariant = variantsForUi.find(
      (variant) =>
        String(variant.id ?? variant.variant_id ?? "") === String(variantId)
    );
    if (nextVariant && !isVariantAvailable(nextVariant)) return;
    setSelectedVariantIds((prev) => {
      const next = [...prev];
      next[index] = String(variantId);
      return next;
    });
  };

  return (
    <div
      className={styles.card}
      style={{
        fontFamily,
        background: design.shellBackground || "#ffffff",
        borderWidth: px(design.shellBorderWidth, 1),
        borderStyle: "solid",
        borderColor: design.shellBorderColor || "#e5e7eb",
        borderRadius: px(design.cardBorderRadius, 12),
        padding: px(design.shellPadding, 16),
        "--vd-accent": accent,
        "--vd-selected-bg": design.selectedBackground || "#F5F0FF",
        "--vd-offer-bg": design.offerBackground || "#ffffff",
        "--vd-offer-border": design.offerBorderColor || "#e5e7eb",
        "--vd-offer-border-w": px(design.offerBorderWidth, 1),
        "--vd-offer-radius": px(design.offerBorderRadius, 10),
        "--vd-title-size": px(design.titleSize, 16),
        "--vd-title-color": design.titleColor || "#111827",
        "--vd-subtitle-size": px(design.subtitleSize, 13),
        "--vd-subtitle-color": design.subtitleColor || "#6b7280",
        "--vd-header-badge-size": px(design.headerBadgeSize, 11),
        "--vd-header-badge-bg": design.headerBadgeBackground || "#E8F8EE",
        "--vd-header-badge-fg": design.headerBadgeTextColor || "#1B7A3D",
        "--vd-offer-title-size": px(design.offerTitleSize, 14),
        "--vd-offer-title-color": design.offerTitleColor || "#111827",
        "--vd-offer-sub-size": px(design.offerSubtitleSize, 12),
        "--vd-offer-sub-color": design.offerSubtitleColor || "#6b7280",
        "--vd-price-size": px(design.priceSize, 15),
        "--vd-price-color": design.priceColor || "#111827",
        "--vd-compare-size": px(design.compareSize, 12),
        "--vd-compare-color": design.compareColor || "#9ca3af",
        "--vd-badge-bg": design.badgeBackground || "#EDE7FF",
        "--vd-badge-fg": design.badgeTextColor || "#5B3FD4",
        "--vd-badge-size": px(design.badgeSize, 11),
        "--vd-corner-bg": design.cornerBadgeBackground || "#111111",
        "--vd-corner-fg": design.cornerBadgeTextColor || "#FFFFFF",
        "--vd-corner-size": px(design.cornerBadgeSize, 10),
        "--vd-promo-bg": design.promoBackground || "#E8F8EE",
        "--vd-promo-fg": design.promoTextColor || "#1B7A3D",
        "--vd-promo-size": px(design.promoSize, 13),
        "--vd-promo-radius": px(design.promoBorderRadius, 8),
        "--vd-timer-bg": design.countdownBackground || "#0f2744",
        "--vd-timer-fg": design.countdownTextColor || "#f9a8d4",
        "--vd-timer-size": px(design.countdownSize, 13),
        "--vd-variant-size": px(design.variantSize, 12),
        "--vd-variant-color": design.variantColor || "#4b5563",
        "--vd-variant-bg": design.variantBackground || "#ffffff",
        "--vd-variant-border": design.variantBorderColor || "#e5e7eb",
        "--vd-variant-border-w": px(design.variantBorderWidth, 1),
        "--vd-variant-radius": px(design.variantBorderRadius, 8),
        ...(isHorizontal
          ? { "--vd-items-per-row": String(itemsPerRow) }
          : {}),
      }}
    >
      {(layout.showTitle !== false ||
        (layout.showBadge !== false && layout.badge) ||
        (layout.showSubtitle !== false && layout.subtitle)) ? (
        <div className={styles.header}>
          {layout.showTitle !== false ||
          (layout.showBadge !== false && layout.badge) ? (
            <div className={styles.titleRow}>
              {layout.showTitle !== false ? (
                <h3 className={styles.title}>
                  {layout.title || "Volume Discount"}
                </h3>
              ) : null}
              {layout.showBadge !== false && layout.badge ? (
                <span className={styles.headerBadge}>{layout.badge}</span>
              ) : null}
            </div>
          ) : null}
          {layout.showSubtitle !== false && layout.subtitle ? (
            <p className={styles.subtitle}>{layout.subtitle}</p>
          ) : null}
        </div>
      ) : null}

      {layout.countdown?.enabled ? (
        <div className={styles.timer}>
          {applyVolumeDiscountPlaceholders(layout.countdown.title || "", {
            timer: timerText,
          })}
        </div>
      ) : null}

      <div
        className={`${styles.offers} ${isHorizontal ? styles.offersHorizontal : ""}`}
      >
        {layout.offers.map((offer, index) => {
          const pricing = calculateVolumeDiscountOfferPrice(
            offer,
            pricedUnit,
            offerMode
          );
          const isSelected = index === selectedIndex;
          const offerUnits = getVolumeDiscountOfferUnitCount(offer, offerMode);
          const discountLabel = applyVolumeDiscountPlaceholders(
            resolveOfferDiscountLabelTemplate(offer, offerMode),
            {
              savedPercentage: pricing.savedPercentage,
              savedTotal: formatMoney(pricing.saved, currency),
            }
          );
          const subtitle = applyVolumeDiscountPlaceholders(
            offer.subtitle || "",
            {
              savedPercentage: pricing.savedPercentage,
              savedTotal: formatMoney(pricing.saved, currency),
            }
          );
          const imageStackCount = Math.min(
            Math.max(1, offerUnits),
            MAX_PRODUCT_IMAGE_STACK
          );
          const productThumb = showCurrentPageProduct ? (
            <div
              className={styles.productImageStack}
              data-count={imageStackCount}
              aria-hidden={offerUnits > 1 ? undefined : true}
            >
              {Array.from({ length: imageStackCount }).map((_, imgIndex) => (
                <img
                  key={imgIndex}
                  className={styles.productImage}
                  src={productImageUrl}
                  alt={
                    imgIndex === 0
                      ? product?.title || "Product"
                      : ""
                  }
                  style={{ zIndex: MAX_PRODUCT_IMAGE_STACK - imgIndex }}
                />
              ))}
              {offerUnits > MAX_PRODUCT_IMAGE_STACK ? (
                <span className={styles.productImageMore}>
                  +{offerUnits - MAX_PRODUCT_IMAGE_STACK}
                </span>
              ) : null}
            </div>
          ) : null;

          const variantBlock =
            isSelected && showVariantSelectors && offerUnits >= 1 ? (
              <div
                className={styles.variants}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                {Array.from({ length: offerUnits }).map((_, i) => (
                  <label key={i} className={styles.variantRow}>
                    <span className={styles.variantIndex}>{i + 1}:</span>
                    <select
                      className={styles.variantSelect}
                      value={selectedVariantIds[i] || ""}
                      onChange={(event) =>
                        updateVariantAt(i, event.target.value)
                      }
                      aria-label={`Variant for unit ${i + 1}`}
                    >
                      {variantsForUi.map((variant) => {
                        const id = String(
                          variant.id ?? variant.variant_id ?? ""
                        );
                        const available = isVariantAvailable(variant);
                        return (
                          <option key={id} value={id} disabled={!available}>
                            {variantLabel(variant)}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                ))}
              </div>
            ) : null;

          if (isHorizontal) {
            return (
              <button
                key={offer.id || index}
                type="button"
                className={`${styles.offer} ${styles.offerHorizontal} ${
                  isSelected ? styles.offerSelected : ""
                }`}
                onClick={() => {
                  setSelectedIndex(index);
                  if (typeof onOfferSelect === "function") {
                    onOfferSelect({
                      offer,
                      index,
                      previouslySelectedIndex: selectedIndex,
                    });
                  }
                }}
              >
                {offer.badge ? (
                  <span className={styles.cornerBadgeTop}>{offer.badge}</span>
                ) : null}
                {productThumb}
                <span className={styles.offerTitle}>
                  {offer.title || `Offer ${index + 1}`}
                </span>
                {discountLabel ? (
                  <span className={styles.saveBadge}>{discountLabel}</span>
                ) : null}
                {subtitle ? (
                  <span className={styles.offerSubtitle}>{subtitle}</span>
                ) : null}
                <div className={styles.pricesHorizontal}>
                  <span className={styles.price}>
                    {formatMoney(pricing.final, currency)}
                  </span>
                  {pricing.saved > 0 ? (
                    <span className={styles.compare}>
                      {formatMoney(pricing.original, currency)}
                    </span>
                  ) : null}
                </div>
                {variantBlock}
              </button>
            );
          }

          return (
            <button
              key={offer.id || index}
              type="button"
              className={`${styles.offer} ${isSelected ? styles.offerSelected : ""}`}
              onClick={() => {
                setSelectedIndex(index);
                if (typeof onOfferSelect === "function") {
                  onOfferSelect({
                    offer,
                    index,
                    previouslySelectedIndex: selectedIndex,
                  });
                }
              }}
            >
              {offer.badge ? (
                <span className={styles.cornerBadge}>{offer.badge}</span>
              ) : null}
              <span
                className={`${styles.radio} ${isSelected ? styles.radioOn : ""}`}
                aria-hidden
              />
              {productThumb}
              <div className={styles.offerBody}>
                <div className={styles.offerTitleRow}>
                  <span className={styles.offerTitle}>
                    {offer.title || `Offer ${index + 1}`}
                  </span>
                  {discountLabel ? (
                    <span className={styles.saveBadge}>{discountLabel}</span>
                  ) : null}
                </div>
                {subtitle ? (
                  <span className={styles.offerSubtitle}>{subtitle}</span>
                ) : null}
                {variantBlock}
              </div>
              <div className={styles.prices}>
                {pricing.saved > 0 ? (
                  <span className={styles.compare}>
                    {formatMoney(pricing.original, currency)}
                  </span>
                ) : null}
                <span className={styles.price}>
                  {formatMoney(pricing.final, currency)}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {layout.promoBanner?.enabled !== false && layout.promoBanner?.text ? (
        <div className={styles.promo}>{layout.promoBanner.text}</div>
      ) : null}
    </div>
  );
}

export function isVolumeDiscountPreview(popup) {
  return (
    popup?.generalSettings?.isStaticVolumeDiscount === true ||
    popup?.generalSettings?.popupLayoutId === "volume-discount-v1" ||
    String(popup?.popUpType || "").trim() === "Volume Discount"
  );
}
